import React, { useCallback, useEffect, FC, useState } from 'react'
import { last } from 'lodash/fp'
import first from 'lodash/first'
import { DateUtil, TIME } from '@utils/dateUtil'
import { RecurrenceT, TypePattern } from '@providers'
import { BookingDayPicker } from './components'
import { useBookingDayPicker } from './components/BookingDayPicker'
import CalendarTimeSelector from './components/CalendarTimeSelector'
import BookingRecurrence from './components/BookingRecurrence'
import useBookingRecurrence from './components/BookingRecurrence/hooks/useBookingRecurrence'
import { UseFloorHook } from '@providers/mapProvider'

type BookingErrorT = {
  message: string
  type?: string
}

type BookingCalendarPropsT = {
  title?: string
  timezone?: string
  allDaySetting: boolean
  is24Hr: boolean
  enableMultipleDays?: boolean
  numOfViews?: number
  defaultTimeFrom?: string
  defaultTimeTo?: string
  maxSelectedDays?: number
  headerRender?: JSX.Element
  constraints?: { first?: DateUtil; last?: DateUtil }
  options: Array<{ from: []; to: [] }>
  recurrence?: RecurrenceT
  selectedDates?: (DateUtil | any)[]
  showTime?: boolean
  showRecurrence?: boolean
  bookedTimes?: DateUtil[]
  setRecurredBooking?: (param: Partial<RecurrenceT>, fromDate: DateUtil) => void
  onDayClick?: (day: DateUtil) => void
  onAllDayToggle?: (value: boolean) => boolean
  onTimeChange?: ({ fromDate, toDate }: { fromDate: DateUtil; toDate: DateUtil }) => void
  onDateChange?: (dates: DateUtil[]) => void
  validateDateTime?: boolean
  isNotBookDesk?: boolean
  floor?: UseFloorHook
}

const BookingCalendar: FC<BookingCalendarPropsT> = ({
  title,
  timezone = DateUtil.defaultTz(),
  allDaySetting,
  defaultTimeFrom = '00:00',
  defaultTimeTo = '23:59',
  recurrence,
  bookedTimes = [],
  enableMultipleDays = false,
  maxSelectedDays = 0,
  constraints = {},
  numOfViews = 1,
  onDayClick,
  onAllDayToggle,
  onTimeChange,
  headerRender,
  setRecurredBooking = () => {},
  selectedDates = [DateUtil.tz(timezone), DateUtil.tz(timezone)],
  onDateChange,
  showRecurrence,
  showTime,
  validateDateTime = true,
  isNotBookDesk,
  floor,
}) => {
  //TODO implement error handlers
  const [allDayFlag, setAllDay] = useState(false)
  const [dates, setDates] = useState(selectedDates)
  const [isTimeChange, setTimeChange] = useState(false)
  const [hasDateTimeChange, setDateTimeChange] = useState<boolean | null>(null)
  // const [error, setError] = useState<Error | null>(null)
  // const [bookingError, setBookingError] = useState<BookingErrorT | null>(null)

  const calendarConstraints = {
    first: constraints.first || DateUtil.tz(timezone).startOf(TIME.DAYS),
    last: constraints.last || DateUtil.tz(timezone).endOf(TIME.DAYS).add(2, TIME.YEARS),
  }
  const isRecurringCalendar =
    floor?.isBookDeskPage &&
    (recurrence || { typePattern: TypePattern.NEVER }).typePattern !== TypePattern.NEVER
  const allDay = allDaySetting || allDayFlag
  let maxSelDays = enableMultipleDays ? (maxSelectedDays === 0 ? 365 : maxSelectedDays) : 1
  maxSelDays = isRecurringCalendar ? 365 : maxSelDays

  const setAllDayCallback = (allDay: boolean) => {
    setTimeChange(false)
    const value = onAllDayToggle ? onAllDayToggle(allDay) : allDay
    setAllDay(value)
    setDateTimeChange(true)
  }

  const { selectDaysRange, selected } = useBookingDayPicker({
    selectedDates: dates,
    allDay,
    defaultTimeFrom,
    defaultTimeTo,
    enableMultipleDays: isRecurringCalendar ? false : enableMultipleDays,
    timezone,
    maxSelectedDays: maxSelDays,
    isTimeChange,
    onDateChange: setDates,
    validateDateTime,
    recurrence,
  })

  const dateRecurrence = useBookingRecurrence({
    fromDate: selected[0],
    recurrence,
    setRecurredBooking,
    timezone,
    maxDate: calendarConstraints.last,
  })

  // handlers
  const onDayClickHandler = (date: DateUtil) => {
    setTimeChange(false)
    onDayClick?.(date)
    selectDaysRange(date)
    if (isRecurringCalendar) {
      setRecurredBooking({ daysOfWeek: recurrence?.daysOfWeek }, date)
    }
    setDateTimeChange(!hasDateTimeChange)
  }
  const onRecurrenceHandler = (params: Partial<RecurrenceT>) => {
    selectDaysRange(selected[0])
    setRecurredBooking(params, selected[0])
  }
  const onTimeSelected = ({ fromDate, toDate }: { fromDate: DateUtil; toDate: DateUtil }) => {
    setTimeChange(true)
    setDates([fromDate, toDate])
    setDateTimeChange(!hasDateTimeChange)
  }

  useEffect(() => {
    if (hasDateTimeChange !== null) {
      onDateChange?.(selected)
    }
  }, [hasDateTimeChange])

  return (
    <>
      <div className="mb-4">
        <BookingDayPicker
          isRecurrence={isRecurringCalendar as boolean}
          selected={isRecurringCalendar ? dateRecurrence : selected}
          numOfViews={numOfViews}
          headerRender={headerRender}
          timezone={timezone}
          onDayClick={onDayClickHandler}
          constraints={calendarConstraints}
          floor={floor as UseFloorHook}
        />
      </div>
      {showTime && (
        <div className="mb-4">
          <CalendarTimeSelector
            allDay={allDay}
            defaultTimeFrom={defaultTimeFrom}
            defaultTimeTo={defaultTimeTo}
            bookedTimes={bookedTimes}
            timezone={timezone}
            selected={selected}
            onAllDayChange={setAllDayCallback}
            onTimeChange={onTimeSelected}
            isNotBookDesk={isNotBookDesk as boolean}
          />
        </div>
      )}
      {showRecurrence && recurrence && floor?.isBookDeskPage && (
        <div className="mb-4">
          <BookingRecurrence
            recurrence={recurrence}
            selected={selected}
            timezone={timezone}
            setRecurredBooking={onRecurrenceHandler}
            constraints={calendarConstraints}
          />
        </div>
      )}
    </>
  )
}

export default BookingCalendar

const createBookingError = (error: BookingErrorT): BookingErrorT => {
  return {
    message: error.message,
    type: error.type || createBookingError.type,
  }
}

createBookingError.type = 'incorrect_time'
